import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, FileText, Search, Filter, Eye, CheckCircle, CheckCircle2, XCircle, Clock, Download, Edit } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import WorkOrderForm from './WorkOrderForm';
import { toast } from 'sonner';

export default function WorkOrdersModule() {
  const { user, isAdmin } = useAuth();
  const isAccounting = user?.role === 'accounting';
  const canManage = isAdmin || isAccounting;
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedWO, setSelectedWO] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editWO, setEditWO] = useState(null);
  const [approvalComments, setApprovalComments] = useState('');
  const [approving, setApproving] = useState(false);
  const [convertingInvoice, setConvertingInvoice] = useState(false);

  useEffect(() => {
    loadWorkOrders();
  }, [statusFilter]);

  const loadWorkOrders = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await api.get('/work-orders', { params });
      setWorkOrders(res.data);
    } catch (err) {
      console.error('Failed to load work orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-slate-100 text-slate-700 border-slate-300',
      in_progress: 'bg-blue-100 text-blue-700 border-blue-300',
      completed: 'bg-green-100 text-green-700 border-green-300',
      approved: 'bg-cyan-100 text-cyan-700 border-cyan-300',
      rejected: 'bg-red-100 text-red-700 border-red-300',
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const filteredWorkOrders = workOrders.filter(wo =>
    searchQuery === '' ||
    wo.ticket_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    wo.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    wo.technician_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleApproveWO = async (approved) => {
    if (!selectedWO) return;
    try {
      setApproving(true);
      const res = await api.post(`/work-orders/${selectedWO.id}/approve`, {
        approved,
        comments: approvalComments || null,
      });
      setSelectedWO(res.data);
      setApprovalComments('');
      toast.success(approved ? 'Work order approved — PDF generated' : 'Work order rejected');
      loadWorkOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update work order');
    } finally {
      setApproving(false);
    }
  };

  const previewPDF = async (woId) => {
    try {
      const response = await api.get(`/work-orders/${woId}/preview-pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('Failed to preview PDF:', err);
      alert('Could not generate preview PDF.');
    }
  };

  const downloadBrandedPDF = async (woId, ticketNumber) => {
    try {
      const response = await api.get(`/work-orders/${woId}/pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      // Open inline in a new tab — browsers expose a Save button.
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('Failed to download branded PDF:', err);
      toast.error(err.response?.data?.detail || 'Could not generate PDF.');
    }
  };

  const downloadPDF = async (woId, ticketNumber) => {
    try {
      const response = await api.get(`/work-orders/${woId}/download-pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `work_order_${ticketNumber || woId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('PDF not available yet. Use Preview PDF instead.');
    }
  };

  const stats = {
    total: workOrders.length,
    pending: workOrders.filter(w => w.status === 'pending').length,
    in_progress: workOrders.filter(w => w.status === 'in_progress').length,
    completed: workOrders.filter(w => w.status === 'completed').length,
    approved: workOrders.filter(w => w.status === 'approved').length,
  };

  if (selectedWO) {
    return (
      <div className="flex-1 p-8 overflow-auto">
        <div className="flex items-center justify-between mb-6">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedWO(null)}
            className="text-slate-600"
          >
            ← Back to Work Orders
          </Button>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => downloadBrandedPDF(selectedWO.id, selectedWO.ticket_number)}
              className="bg-cyan-500 hover:bg-cyan-600 rounded-full"
              data-testid="download-branded-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button
              onClick={() => previewPDF(selectedWO.id)}
              variant="outline"
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-50 rounded-full"
              data-testid="preview-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Legacy preview
            </Button>
            {selectedWO.pdf_url && (
              <Button
                onClick={() => downloadPDF(selectedWO.id, selectedWO.ticket_number)}
                variant="outline"
                className="border-slate-300 rounded-full"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Approved PDF
              </Button>
            )}
          </div>
        </div>

        <Card className="bg-white border-slate-200">
          <CardHeader className="border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-slate-800">Work Order Details</CardTitle>
                <p className="text-sm text-slate-500 mt-1">Ticket: {selectedWO.ticket_number}</p>
              </div>
              <Badge className={getStatusColor(selectedWO.status) + ' border'}>
                {getStatusIcon(selectedWO.status)}
                <span className="ml-1">{selectedWO.status.replace('_', ' ').toUpperCase()}</span>
              </Badge>
            </div>
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="text-xs text-slate-500 mb-1">Customer</p>
                <p className="text-sm font-medium text-slate-800">{selectedWO.customer_name}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Technician</p>
                <p className="text-sm font-medium text-slate-800">{selectedWO.technician_name}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Service Type</p>
                <Badge variant="outline">{selectedWO.remote_onsite}</Badge>
              </div>
            </div>

            {selectedWO.work_done && (
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Work Performed</h3>
                <p className="text-sm text-slate-700 whitespace-pre-wrap p-4 bg-slate-50 rounded-lg">
                  {selectedWO.work_done}
                </p>
              </div>
            )}

            {selectedWO.parts_used && selectedWO.parts_used.length > 0 && (
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Parts Used</h3>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left">Part</th>
                        <th className="px-4 py-2 text-center">Qty</th>
                        <th className="px-4 py-2 text-right">Cost</th>
                        <th className="px-4 py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {selectedWO.parts_used.map((part, i) => (
                        <tr key={`${part.name}-${part.quantity}-${i}`}>
                          <td className="px-4 py-2">{part.name}</td>
                          <td className="px-4 py-2 text-center">{part.quantity}</td>
                          <td className="px-4 py-2 text-right">${part.cost.toFixed(2)}</td>
                          <td className="px-4 py-2 text-right font-medium">
                            ${(part.cost * part.quantity).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Inventory items */}
            {selectedWO.inventory_items && selectedWO.inventory_items.length > 0 && (
              <div data-testid="wo-detail-inventory">
                <h3 className="font-semibold text-slate-800 mb-2">Inventory Items</h3>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left">Item</th>
                        <th className="px-4 py-2 text-center">Qty</th>
                        <th className="px-4 py-2 text-right">Unit price</th>
                        <th className="px-4 py-2 text-right">Line total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {selectedWO.inventory_items.map((it, i) => (
                        <tr key={`${it.inventory_id}-${i}`}>
                          <td className="px-4 py-2">{it.name}{it.sku ? <span className="text-xs text-slate-500 block">{it.sku}</span> : null}</td>
                          <td className="px-4 py-2 text-center">{it.quantity}</td>
                          <td className="px-4 py-2 text-right">${(parseFloat(it.unit_price) || 0).toFixed(2)}</td>
                          <td className="px-4 py-2 text-right font-medium">${(parseFloat(it.line_total) || (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity, 10) || 0)).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Cost summary */}
            {(selectedWO.total_amount != null || selectedWO.hourly_rate != null) && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-1 text-sm" data-testid="wo-detail-totals">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Hourly rate ({selectedWO.remote_onsite}) × {selectedWO.time_spent_hours || 0}h</span>
                  <span className="font-medium text-slate-800">{selectedWO.hourly_rate != null ? `$${(selectedWO.labor_total || 0).toFixed(2)}` : 'No client rate set'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Parts</span>
                  <span className="font-medium text-slate-800">${(selectedWO.parts_total || 0).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Inventory</span>
                  <span className="font-medium text-slate-800">${(selectedWO.inventory_total || 0).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between pt-2 mt-2 border-t border-slate-200">
                  <span className="text-slate-700 font-semibold">Total</span>
                  <span className="text-lg font-bold text-slate-900" data-testid="wo-detail-grand-total">${(selectedWO.total_amount || 0).toFixed(2)}</span>
                </div>
                {selectedWO.invoice_number && (
                  <p className="text-xs text-emerald-700 pt-2">Invoiced as <strong>{selectedWO.invoice_number}</strong>{selectedWO.invoiced_at ? ` on ${new Date(selectedWO.invoiced_at).toLocaleDateString()}` : ''}</p>
                )}
              </div>
            )}

            {(selectedWO.customer_signature || selectedWO.technician_signature || selectedWO.signature_image) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(selectedWO.customer_signature || selectedWO.signature_image) && (
                  <div data-testid="wo-customer-signature">
                    <h3 className="font-semibold text-slate-800 mb-2">Customer Signature</h3>
                    <div className="border-2 border-slate-200 rounded-lg p-4 bg-white">
                      <img
                        src={selectedWO.customer_signature || selectedWO.signature_image}
                        alt="Customer signature"
                        className="max-w-full h-auto"
                      />
                    </div>
                  </div>
                )}
                {selectedWO.technician_signature && (
                  <div data-testid="wo-technician-signature">
                    <h3 className="font-semibold text-slate-800 mb-2">Technician Signature</h3>
                    <div className="border-2 border-slate-200 rounded-lg p-4 bg-white">
                      <img
                        src={selectedWO.technician_signature}
                        alt="Technician signature"
                        className="max-w-full h-auto"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Admin / Accounting review actions */}
            {canManage && !['approved', 'rejected', 'invoiced'].includes(selectedWO.status) && (
              <div className="border-t border-slate-200 pt-6">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-600" />
                  {isAdmin ? 'Admin Review' : 'Accounting Review'}
                </h3>
                <div className="space-y-3">
                  <Textarea
                    value={approvalComments}
                    onChange={(e) => setApprovalComments(e.target.value)}
                    placeholder="Optional comments for the technician..."
                    className="bg-white border-slate-200 min-h-[80px]"
                    data-testid="approval-comments"
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      type="button"
                      onClick={() => handleApproveWO(true)}
                      disabled={approving}
                      className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full"
                      data-testid="approve-wo-btn"
                    >
                      {approving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                      )}
                      Approve & Generate PDF
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleApproveWO(false)}
                      disabled={approving}
                      className="border-red-300 text-red-600 hover:bg-red-50 rounded-full"
                      data-testid="reject-wo-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setEditWO(selectedWO)}
                      className="text-slate-600 hover:text-cyan-700"
                      data-testid="edit-wo-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit while reviewing
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Convert to invoice (admin/accounting only, only on approved WOs) */}
            {canManage && selectedWO.status === 'approved' && !selectedWO.invoice_id && (
              <div className="border-t border-slate-200 pt-6">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  Accounting
                </h3>
                <Button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm('Convert this work order into an invoice? The WO will be marked as invoiced.')) return;
                    try {
                      setConvertingInvoice(true);
                      const res = await api.post(`/work-orders/${selectedWO.id}/convert-to-invoice`, { notes: '' });
                      const inv = res.data?.invoice;
                      if (inv) toast.success(`Invoice ${inv.invoice_number} created — $${(inv.total_amount || 0).toFixed(2)}`);
                      // refresh
                      const reload = await api.get(`/work-orders/${selectedWO.id}`);
                      setSelectedWO(reload.data);
                      loadWorkOrders();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to convert to invoice');
                    } finally {
                      setConvertingInvoice(false);
                    }
                  }}
                  disabled={convertingInvoice}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full"
                  data-testid="convert-invoice-btn"
                >
                  {convertingInvoice ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                  Convert to invoice
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        {/* Edit WO modal */}
        {editWO && (
          <WorkOrderForm
            ticket={{ id: editWO.ticket_id, ticket_type: editWO.remote_onsite?.toLowerCase() === 'onsite' ? 'onsite' : 'remote' }}
            existingWorkOrder={editWO}
            onClose={() => setEditWO(null)}
            onSaved={() => {
              setEditWO(null);
              loadWorkOrders();
              // Refresh selected WO
              (async () => {
                try {
                  const res = await api.get(`/work-orders/${selectedWO.id}`);
                  setSelectedWO(res.data);
                } catch {}
              })();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold mb-2 text-slate-800">Work Orders</h1>
        <p className="text-sm md:text-base text-slate-500">Manage service work orders and approvals</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-4 md:mb-6">
        <Card className="bg-white border-slate-200">
          <CardContent className="p-3 md:p-4">
            <p className="text-xs text-slate-500 mb-1">Total</p>
            <p className="text-xl md:text-2xl font-bold text-slate-800">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-3 md:p-4">
            <p className="text-xs text-slate-500 mb-1">Pending</p>
            <p className="text-xl md:text-2xl font-bold text-slate-600">{stats.pending}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-3 md:p-4">
            <p className="text-xs text-slate-500 mb-1">In Progress</p>
            <p className="text-xl md:text-2xl font-bold text-blue-600">{stats.in_progress}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-3 md:p-4">
            <p className="text-xs text-slate-500 mb-1">Completed</p>
            <p className="text-xl md:text-2xl font-bold text-green-600">{stats.completed}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-3 md:p-4">
            <p className="text-xs text-slate-500 mb-1">Approved</p>
            <p className="text-xl md:text-2xl font-bold text-cyan-600">{stats.approved}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-4 md:mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by ticket, customer, or technician..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white border-slate-200 h-11 md:h-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 bg-white border-slate-200 h-11 md:h-10">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Work Orders List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
        </div>
      ) : filteredWorkOrders.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-slate-400" />
            <h3 className="text-lg font-medium mb-2 text-slate-800">No work orders found</h3>
            <p className="text-slate-500">Work orders will appear here when created</p>
          </CardContent>
        </Card>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Ticket</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Technician</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Service</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredWorkOrders.map((wo) => (
                <tr key={wo.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <span className="text-sm font-mono text-slate-800">{wo.ticket_number}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-700">{wo.customer_name}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-700">{wo.technician_name}</span>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="outline" className="text-xs">{wo.remote_onsite}</Badge>
                  </td>
                  <td className="px-6 py-4">
                    <Badge className={getStatusColor(wo.status) + ' border text-xs'}>
                      {getStatusIcon(wo.status)}
                      <span className="ml-1">{wo.status.replace('_', ' ')}</span>
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-500">
                      {new Date(wo.created_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadBrandedPDF(wo.id, wo.ticket_number)}
                        className="text-slate-600 hover:text-cyan-700"
                        data-testid={`download-branded-pdf-row-${wo.id}`}
                        title="Download branded PDF"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => previewPDF(wo.id)}
                        className="text-slate-600 hover:text-cyan-700"
                        data-testid={`preview-pdf-row-${wo.id}`}
                        title="Legacy preview"
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedWO(wo)}
                        className="text-cyan-600 hover:text-cyan-700"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <WorkOrderForm
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            loadWorkOrders();
          }}
        />
      )}
    </div>
  );
}
